/**
 * For now, chat is the only AI feature that is surfaced through the sidepanel, so this file lives in
 * the llm/chat-library folder.
 */
import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, Divider, Tabs, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {isMcpAiConnectorsFeatureEnabled} from 'llm/isMcpAiConnectorsFeatureEnabled';
import RoutinesPage from 'llm/routines/RoutinesPage';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, NEAR_BLACK} from 'ui-library/Colors';

import AISettingsConnectors from './AISettingsConnectors';
import AISettingsGeneral from './AISettingsGeneral';
import AISettingsSkills from './AISettingsSkills';

const useStyles = createStyles(() => ({
  skillsPanel: {
    overflow: 'hidden',
    '&&': {
      padding: 0,
    },
  },
  skillsPanelInner: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
}));

export const HAS_SEEN_AI_SIDEPANEL_ONBOARDING = 'llm.ai-sidepanel.has-seen-onboarding';

export enum AISettingsTab {
  GENERAL = 'GENERAL',
  SKILLS = 'SKILLS',
  AI_CONNECTORS = 'AI_CONNECTORS',
  ROUTINES = 'ROUTINES',
}

const TAB_LABELS: Record<AISettingsTab, string> = {
  [AISettingsTab.GENERAL]: 'General',
  [AISettingsTab.SKILLS]: 'Skills',
  [AISettingsTab.AI_CONNECTORS]: 'AI Connectors',
  [AISettingsTab.ROUTINES]: 'Background Agents',
};

export function AISettingsContent({
  chatId,
  isExpanded,
  activeTab,
  onTabChange,
  onRestartOnboarding,
}: {
  chatId: string | null;
  isExpanded?: boolean;
  activeTab: AISettingsTab;
  onTabChange: (tab: AISettingsTab) => void;
  onRestartOnboarding?: () => void;
}): JSX.Element {
  const {llmEnableRoutinesTimeBased: timeBasedEnabled, llmEnableRoutinesEventBased: eventBasedEnabled} =
    useRolloutFlags();
  const {classes} = useStyles();
  const routinesEnabled = timeBasedEnabled || eventBasedEnabled;
  const tabs = [
    AISettingsTab.GENERAL,
    AISettingsTab.SKILLS,
    ...(isMcpAiConnectorsFeatureEnabled() ? [AISettingsTab.AI_CONNECTORS] : []),
    ...(routinesEnabled ? [AISettingsTab.ROUTINES] : []),
  ];
  const selectedTab = tabs.includes(activeTab) ? activeTab : AISettingsTab.GENERAL;

  const handleTabChange = (tab: string | null): void => {
    assert(isAISettingsTab(tab) && tabs.includes(tab), `Unexpected tab selected: ${tab}`);
    onTabChange(tab);
  };

  return (
    <Tabs
      orientation="vertical"
      value={selectedTab}
      onTabChange={handleTabChange}
      styles={{
        root: {
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden',
        },
        tabsList: {
          width: 160,
          minWidth: 160,
          padding: '16px 8px',
          borderRight: `1px solid ${GRAY_LIGHT}`,
          gap: 8,
        },
        panel: {
          flex: 1,
          padding: '16px 24px',
          overflowY: 'auto',
          minWidth: 0,
        },
      }}
      classNames={{
        panel: 'scrollbar-override',
      }}
    >
      <Tabs.List>
        <Text fw={700} size="sm" py={4} px={8} style={{color: NEAR_BLACK}}>
          AI Chat Settings
        </Text>
        {tabs.map((tab) => (
          <Tabs.Tab
            key={tab}
            value={tab}
            {...getTestProps({
              component: 'AISettings',
              element: 'navItem',
              key: TAB_LABELS[tab],
            })}
          >
            {TAB_LABELS[tab]}
          </Tabs.Tab>
        ))}
      </Tabs.List>

      <Tabs.Panel value={AISettingsTab.GENERAL}>
        <AISettingsGeneral onRestartOnboarding={onRestartOnboarding} />
        {chatId && (
          <>
            <Divider mt={24} />
            <div className={css(styles.exportButtonWrapper)}>
              <Button
                buttonStyle={ButtonStyle.SECONDARY}
                icon="audit-trail"
                onClick={() => {
                  AuditActions.exportAuditLogs([chatId]);
                }}
                size={ButtonSize.SMALL}
                text={EXPORT_AUDIT_LOG_DISPLAY_STRING}
                {...getTestProps({
                  component: 'AISidepanelV2',
                  element: 'exportAuditLogButton',
                })}
              />
            </div>
          </>
        )}
      </Tabs.Panel>

      <Tabs.Panel value={AISettingsTab.SKILLS} className={classes.skillsPanel}>
        <Box className={classes.skillsPanelInner}>
          <AISettingsSkills isExpanded={isExpanded} />
        </Box>
      </Tabs.Panel>

      <Tabs.Panel value={AISettingsTab.AI_CONNECTORS}>
        {activeTab === AISettingsTab.AI_CONNECTORS && <AISettingsConnectors />}
      </Tabs.Panel>

      {routinesEnabled && (
        <Tabs.Panel value={AISettingsTab.ROUTINES}>
          {activeTab === AISettingsTab.ROUTINES && (
            <RoutinesPage
              timeBasedEnabled={timeBasedEnabled}
              eventBasedEnabled={eventBasedEnabled}
              isExpanded={Boolean(isExpanded)}
            />
          )}
        </Tabs.Panel>
      )}
    </Tabs>
  );
}

function isAISettingsTab(tab: string | null): tab is AISettingsTab {
  // Type guard to ensure the tab is a valid AISettingsTab
  return tab != null && Object.values(AISettingsTab).includes(tab as AISettingsTab);
}

const styles = StyleSheet.create({
  exportButtonWrapper: {
    marginTop: 16,
  },
});
